// What made each case different, read off the cases in scope alone. Nothing here is a benchmark:
// "long", "rare" and "more than usual" are all relative to this process's own distribution, so
// the same rules hold for a process whose cases take seconds and one whose cases take days.
// Notes are data (a kind and its readings); the panel turns them into words.
import { median, quantile } from "./processDurationStats";
import { type ProcessInstance, stepWaitSeconds } from "./processGraphModel";

// Below this many cases, quartiles and "rare" say more about the sample size than the process.
const MIN_CASES_FOR_DISTRIBUTION = 5;
// A step or app seen in fewer than this share of cases is an unusual part of the process.
const RARE_SHARE = 0.15;
// Time in an app below this is incidental (a glance at a notification), not a different way of
// doing the work.
const RARE_APP_MIN_SECONDS = 30;
// The shortest pause worth calling a wait at all; matches the Top waits list's floor.
const MIN_NOTABLE_WAIT_SECONDS = 2 * 60;

export const notableWaitThreshold = (typicalCaseSeconds: number): number =>
  Math.max(MIN_NOTABLE_WAIT_SECONDS, typicalCaseSeconds);

export type DurationProfile = {
  count: number;
  q1: number;
  median: number;
  q3: number;
  /** Durations above this are far outside the process's usual range; null for small samples. */
  outlierAbove: number | null;
};

/**
 * The line past which a value is an outlier: Tukey's upper fence, but never under twice the
 * median — a tight process would otherwise flag cases that took barely longer than the rest.
 */
export const durationProfile = (values: number[]): DurationProfile => {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const q3 = quantile(sorted, 0.75);
  const mid = median(sorted);
  return {
    count: sorted.length,
    q1,
    median: mid,
    q3,
    outlierAbove:
      sorted.length >= MIN_CASES_FOR_DISTRIBUTION ? Math.max(q3 + 1.5 * (q3 - q1), 2 * mid) : null,
  };
};

/**
 * What a note's colour says: red for a significant delay, orange for rework, purple for an unusual
 * path, blue for an informational difference — and neutral for a reading that needs no attention.
 * Colour is kept for what stands out; if every row were tinted, none would.
 */
export type CaseNoteTone = "delay" | "attention" | "variation" | "info" | "neutral";

type CaseNoteReading =
  /** A pause between two steps longer than a typical case takes to do its work. */
  | { kind: "wait"; seconds: number; beforeStepId: string }
  /** Came back to steps it had already left, more often than the `typical` case does. */
  | { kind: "rework"; loops: number; stepId: string; typical: number }
  /** Includes more rarely seen steps than the typical case; `cases` do the first of them. */
  | {
      kind: "variation";
      stepId: string;
      stepIds: string[];
      more: number;
      cases: number;
    }
  /** Worked in an app only `cases` cases use. */
  | { kind: "app"; app: string; cases: number }
  /** Took many more steps than usual. */
  | { kind: "steps"; count: number; typical: number };

export type CaseNote = { tone: CaseNoteTone } & CaseNoteReading;

const TONE_ORDER: CaseNoteTone[] = ["delay", "attention", "variation", "info", "neutral"];

/**
 * Returns to steps the case had already left. A step repeated back to back (calling one prospect
 * after another) is the work itself, not rework; coming back to it after something else is.
 */
export const caseLoops = (instance: ProcessInstance): { count: number; stepIds: string[] } => {
  const seen = new Set<string>();
  const returned: string[] = [];
  let previous: string | null = null;
  for (const { nodeId } of instance.steps) {
    if (nodeId === previous) continue;
    if (seen.has(nodeId)) returned.push(nodeId);
    seen.add(nodeId);
    previous = nodeId;
  }
  return { count: returned.length, stepIds: returned };
};

const mostFrequent = (values: string[]): string => {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0][0];
};

/** The case's longest pause between two steps, and the step it was waiting to start. */
const longestWait = (
  steps: ProcessInstance["steps"],
): { seconds: number; beforeStepId: string } | null => {
  let longest: { seconds: number; beforeStepId: string } | null = null;
  for (const [index, seconds] of stepWaitSeconds(steps).entries()) {
    if (seconds !== null && (!longest || seconds > longest.seconds)) {
      longest = { seconds, beforeStepId: steps[index + 1].nodeId };
    }
  }
  return longest;
};

const appsUsed = (instance: ProcessInstance): Set<string> => {
  const seconds = new Map<string, number>();
  for (const step of instance.steps) {
    for (const [app, value] of Object.entries(step.appSeconds ?? {})) {
      seconds.set(app, (seconds.get(app) ?? 0) + value);
    }
  }
  return new Set([...seconds].filter(([, value]) => value >= RARE_APP_MIN_SECONDS).map(([a]) => a));
};

export type CaseReading = {
  instance: ProcessInstance;
  duration: number;
  /** Most notable first; empty means the case followed the usual flow. */
  notes: CaseNote[];
  /** Far outside the usual duration range for this process. */
  outlier: boolean;
};

export type CaseSubset = "all" | "longest" | "waits" | "variations" | "rework";

export type CaseAnalysis = {
  cases: CaseReading[];
  /** Cases in scope, the denominator of every "only N of M cases" in the notes. */
  total: number;
  profile: DurationProfile;
  /** A wait at least this long gets a note. */
  notableWaitSeconds: number;
  /** A wait at least this long is painted as a significant delay. */
  severeWaitSeconds: number;
  subsets: Record<CaseSubset, Set<string>>;
};

export const analyzeCases = (
  instances: ProcessInstance[],
  durationOutlierAbove?: number | null,
): CaseAnalysis => {
  const total = instances.length;
  const profile = durationProfile(instances.map(instance => instance.source.avgDuration));
  // Duration filtering keeps the benchmark from the scope before that filter was applied.
  if (durationOutlierAbove !== undefined) profile.outlierAbove = durationOutlierAbove;
  const distribution = total >= MIN_CASES_FOR_DISTRIBUTION;
  // A wait is only worth a note once it outlasts the work of a typical case: short pauses are how
  // desktop work is done. Where long waits are the norm (a process that waits on suppliers), only
  // the longest quarter of them is painted as a delay, so the colour still singles cases out.
  const notableWaitSeconds = notableWaitThreshold(profile.median);
  const longestWaits = instances.map(instance => longestWait(instance.steps));
  const severeWaitSeconds = Math.max(
    notableWaitSeconds,
    durationProfile(longestWaits.map(wait => wait?.seconds ?? 0)).q3,
  );

  const stepCases = new Map<string, number>();
  const appCases = new Map<string, number>();
  const loops = instances.map(caseLoops);
  const apps = instances.map(appsUsed);
  for (const [index, instance] of instances.entries()) {
    for (const nodeId of new Set(instance.steps.map(step => step.nodeId))) {
      stepCases.set(nodeId, (stepCases.get(nodeId) ?? 0) + 1);
    }
    for (const app of apps[index]) appCases.set(app, (appCases.get(app) ?? 0) + 1);
  }
  const rare = (count: number | undefined) =>
    distribution && count !== undefined && count / total < RARE_SHARE;
  const rareSteps = instances.map(instance =>
    [...new Set(instance.steps.map(step => step.nodeId))].filter(id => rare(stepCases.get(id))),
  );
  // Some processes cycle by nature (call, log, call again) and some have a long tail of one-off
  // steps; there, every case loops or detours. Only a case doing so more than the typical one is
  // worth pointing at.
  const typical = (counts: number[]) => median([...counts].sort((a, b) => a - b));
  const typicalLoops = typical(loops.map(loop => loop.count));
  const typicalRareSteps = typical(rareSteps.map(steps => steps.length));
  const stepCounts = durationProfile(instances.map(instance => instance.steps.length));

  const cases = instances.map((instance, index): CaseReading => {
    const notes: CaseNote[] = [];
    const wait = longestWaits[index];
    if (wait && wait.seconds >= notableWaitSeconds) {
      notes.push({
        kind: "wait",
        tone: wait.seconds >= severeWaitSeconds ? "delay" : "neutral",
        seconds: wait.seconds,
        beforeStepId: wait.beforeStepId,
      });
    }
    const loop = loops[index];
    if (loop.count > typicalLoops) {
      notes.push({
        kind: "rework",
        tone: "attention",
        loops: loop.count,
        stepId: mostFrequent(loop.stepIds),
        typical: typicalLoops,
      });
    }
    const unusual = rareSteps[index];
    if (unusual.length > typicalRareSteps) {
      notes.push({
        kind: "variation",
        tone: "variation",
        stepId: unusual[0],
        stepIds: unusual,
        more: unusual.length - 1,
        cases: stepCases.get(unusual[0]) ?? 0,
      });
    }
    const rareApp = [...apps[index]].find(app => rare(appCases.get(app)));
    if (rareApp) {
      notes.push({
        kind: "app",
        tone: "info",
        app: rareApp,
        cases: appCases.get(rareApp) ?? 0,
      });
    }
    if (
      stepCounts.outlierAbove !== null &&
      instance.steps.length > stepCounts.outlierAbove &&
      stepCounts.median > 0
    ) {
      notes.push({
        kind: "steps",
        tone: "info",
        count: instance.steps.length,
        typical: Math.round(stepCounts.median),
      });
    }
    notes.sort((a, b) => TONE_ORDER.indexOf(a.tone) - TONE_ORDER.indexOf(b.tone));
    const duration = instance.source.avgDuration;
    return {
      instance,
      duration,
      notes,
      outlier: profile.outlierAbove !== null && duration > profile.outlierAbove,
    };
  });

  const idsWhere = (test: (reading: CaseReading) => boolean) =>
    new Set(cases.filter(test).map(reading => reading.instance.id));
  return {
    cases,
    total,
    profile,
    notableWaitSeconds,
    severeWaitSeconds,
    subsets: {
      all: idsWhere(() => true),
      // The slowest quarter, give or take ties.
      longest: idsWhere(reading => total > 1 && reading.duration > profile.q3),
      waits: idsWhere(reading => reading.notes.some(note => note.kind === "wait")),
      variations: idsWhere(reading => reading.notes.some(note => note.kind === "variation")),
      rework: idsWhere(reading => reading.notes.some(note => note.kind === "rework")),
    },
  };
};
